import React from 'react';
import { Link2, Star, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { colors } from '@/design-system/colors';
import { billLinesTotal, documentTotals, formatMoney, lineAmount, linesTotal, parseNonNegative, type LineItem } from '@/lib/line-items';
import { GenericSelect } from './GenericForm';
import { GenericItemAutocomplete, type ItemSuggestion } from './GenericItemAutocomplete';

export interface LineSelectOption {
  value: string;
  label: string;
}

interface LineItemsSectionProps<T> {
  lines: LineItem[];
  onChange: (lines: LineItem[]) => void;
  /** matches for what is typed in a line's Item box (the catalog, for a purchase order) */
  searchItems: (term: string) => Promise<Array<ItemSuggestion<T>>>;
  /** an item was picked for a line: the caller decides what the line becomes (its price rules live with the document) */
  onPickItem: (lineKey: string, suggestion: ItemSuggestion<T>) => void;
  /** the cost codes a line can be given (value = the cost code's id, label = what is shown). There is no "none": every line has one. */
  costCodeOptions: LineSelectOption[];
  /**
   * the vendors a line can name: for a line linked to the catalog the vendors that have an offer for its item; for a custom line the
   * company's vendors. Empty / absent = the line shows its vendor as plain text
   */
  vendorOptions?: (line: LineItem) => LineSelectOption[];
  /** the name of a vendor id, for a line that cannot switch */
  vendorName?: (vendorId: string) => string;
  /** the user picked a vendor for a line ('' = cleared, only offered on a custom line) */
  onVendorChange?: (lineKey: string, vendorId: string) => void;
  /** does the line's price come from the item's PREFERRED vendor offer? (a small star beside the price) */
  isPreferredPrice?: (line: LineItem) => boolean;
  /** false: no Vendor column (a bill has ONE vendor of its own). Default true. */
  showVendor?: boolean;
  /**
   * A BILL's lines: adds the Bill Amount column (what the vendor billed for each line -- it can be part of the line's own Amount) and the
   * Total becomes the sum of the Bill Amounts. `pendingOf` is what is still to bill of the purchase-order line a line covers (null = a plain line);
   * a Bill Amount above it is marked, never silently cut.
   */
  billing?: { pendingOf: (line: LineItem) => number | null };
  /**
   * Tax and Other Amount under the lines: Subtotal, Tax (%) with its amount, Other ($) and Total -- the same block for every
   * document that has them. Absent: only the Total row.
   */
  adjustments?: {
    taxPct: string;
    onTaxPctChange: (value: string) => void;
    otherAmount: string;
    onOtherAmountChange: (value: string) => void;
  };
  /** buttons above the table (Add Line, Add from Catalog, ...) */
  toolbar?: React.ReactNode;
  /** the label of the single Total row of a document without tax / other charges here (default "Total:"); a document with its own charges below says "Subtotal:" */
  totalLabel?: string;
  loading?: boolean;
  /** the lines could not be read: the table says so instead of showing an empty list that looks like "no items" */
  loadError?: string | null;
  disabled?: boolean;
}

// the reserved space of the preferred-vendor star, in every price cell and (empty) in the Unit Price header, so the header lines up with the price box
const STAR_SLOT = 'w-3.5 shrink-0 flex items-center justify-center';
const field = 'line-items-field font-form-input generic-form-field';
const moneyInput = `${field} text-right aria-[invalid=true]:border-destructive`;
// entering a number box selects ALL of it, so typing replaces the value instead of landing next to it
const selectAll = (event: React.FocusEvent<HTMLInputElement>) => event.currentTarget.select();
const UNLINKED_NOTE = 'Modified items will be unlinked from the catalog item.';

/**
 * Line Items Section: the editable list of items of a document (a purchase order's items; later any document with lines). Columns: Item
 * (search-as-you-type, with a link icon while it is linked to its catalog item), Cost Code (editable on every line, never empty), Vendor
 * (whose price the line uses; a dropdown), Unit, Quantity, Unit Price (with a star when it is the preferred vendor's offer), Amount and
 * Remove. Quantity and price are edited right in the table; a line's Amount and the one Total row come from lib/line-items.ts (the same
 * arithmetic the server repeats and the server's number is the one stored). Removing a line asks first, with the app's own confirmation.
 *
 * The section knows nothing about catalogs, vendors or prices: the document supplies `searchItems`, `onPickItem`, the vendor options and the
 * preferred-price rule, and decides (in `onChange`) when an edited line is no longer linked to its catalog item. Its compact look lives in
 * design-system/forms.css (the --line-items-* variables) and its header uses the same --table-header-bg as every table.
 */
export function LineItemsSection<T>({ lines, onChange, searchItems, onPickItem, costCodeOptions, vendorOptions, vendorName, onVendorChange, isPreferredPrice, showVendor = true, billing, adjustments, toolbar, totalLabel = 'Total:', loading, loadError, disabled }: LineItemsSectionProps<T>) {
  const patch = (key: string, changes: Partial<LineItem>) => onChange(lines.map((line) => (line.key === key ? { ...line, ...changes } : line)));

  const remove = (line: LineItem, index: number) => {
    const name = line.name.trim();
    if (!window.confirm(`Delete ${name ? `"${name}"` : `item ${index + 1}`}? This action cannot be undone.`)) return;
    onChange(lines.filter((l) => l.key !== line.key));
  };

  const subtotal = billing ? billLinesTotal(lines) : linesTotal(lines);
  const totals = documentTotals(subtotal, adjustments ? adjustments.taxPct : 0, adjustments ? adjustments.otherAmount : 0);
  const taxInvalid = !!adjustments && adjustments.taxPct.trim() !== '' && (parseNonNegative(adjustments.taxPct) === null || parseNonNegative(adjustments.taxPct)! > 100);
  const otherInvalid = !!adjustments && adjustments.otherAmount.trim() !== '' && parseNonNegative(adjustments.otherAmount) === null;

  return (
    // Enter inside a cell must not submit the form that contains this table
    <div className="line-items-section space-y-3" data-testid="line-items" onKeyDown={(event) => { if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') event.preventDefault(); }}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-bold">Items</div>
        <div className="flex items-center gap-2">{toolbar}</div>
      </div>

      {loadError && <div className="generic-form-error" role="alert">{loadError}</div>}

      {loading ? (
        <div className="text-sm text-gray-500">Loading items...</div>
      ) : lines.length === 0 ? (
        <div className="border border-border rounded-md p-6 text-center text-sm text-gray-500" data-testid="line-items-empty">No items yet. Add a line or add items from the catalog.</div>
      ) : (
        <div className="border border-border rounded-md overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="text-left font-table-head border-b border-border bg-table-header">
                <th className="line-items-cell w-[22%]">Item</th>
                <th className="line-items-cell w-[15%]">Cost Code</th>
                {showVendor && <th className="line-items-cell w-[15%]">Vendor</th>}
                <th className="line-items-cell w-[6%]">Unit</th>
                <th className="line-items-cell w-[9%] text-right">Quantity</th>
                <th className="line-items-cell w-[12%]">
                  <div className="flex items-center gap-1">
                    <span className={STAR_SLOT} aria-hidden />
                    <span className="flex-1 text-right">Unit Price</span>
                  </div>
                </th>
                <th className="line-items-cell w-[11%] text-right">Amount</th>
                {billing && <th className="line-items-cell w-[14%] text-right">Bill Amount</th>}
                <th className="line-items-cell w-[5%]"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {lines.map((line, index) => {
                const custom = line.catalogItemId === null;
                const vendors = vendorOptions ? vendorOptions(line) : [];
                const preferred = !!isPreferredPrice && isPreferredPrice(line);
                return (
                  <tr key={line.key} className="align-top" data-testid={`line-${line.key}`}>
                    <td className="line-items-cell">
                      <div className="flex items-center gap-1">
                        {line.catalogItemId && (
                          <span title="Linked to the catalog item" aria-label="Linked to the catalog item" data-testid={`line-linked-${line.key}`} className="shrink-0 text-gray-500">
                            <Link2 className="w-3.5 h-3.5" />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <GenericItemAutocomplete<T>
                            value={line.name}
                            ariaLabel={`Item ${index + 1}`}
                            inputClassName="line-items-field"
                            placeholder="Search items"
                            disabled={disabled}
                            search={searchItems}
                            onChange={(text) => patch(line.key, { name: text })}
                            onPick={(suggestion) => onPickItem(line.key, suggestion)}
                          />
                        </div>
                      </div>
                      {line.unlinkedNotice && <div className="mt-1 text-xs text-gray-500" data-testid={`line-unlinked-${line.key}`}>{UNLINKED_NOTE}</div>}
                    </td>
                    <td className="line-items-cell">
                      <GenericSelect
                        id={`line_cost_code_${index + 1}`}
                        value={line.costCodeId}
                        onValueChange={(value) => patch(line.key, { costCodeId: value })}
                        placeholder="Select cost code"
                        required
                        searchable
                        className="line-items-field"
                        disabled={disabled}
                        contentClassName="max-h-[18.75rem] overflow-y-auto"
                        options={costCodeOptions}
                      />
                    </td>
                    {showVendor && (
                      <td className="line-items-cell text-gray-700">
                        {vendors.length > 0 ? (
                          <GenericSelect
                            id={`line_vendor_${index + 1}`}
                            value={line.vendorId ?? ''}
                            onValueChange={(value) => { if (onVendorChange) onVendorChange(line.key, value); }}
                            placeholder="Select vendor"
                            // a line from the catalog always has one of the item's vendors; a custom line may have none
                            required={!custom}
                            searchable
                            className="line-items-field"
                            disabled={disabled}
                            contentClassName="max-h-[18.75rem] overflow-y-auto"
                            options={vendors}
                          />
                        ) : (
                          <span>{line.vendorId && vendorName ? vendorName(line.vendorId) : '-'}</span>
                        )}
                      </td>
                    )}
                    <td className="line-items-cell text-gray-700">
                      {custom ? <Input aria-label={`Unit ${index + 1}`} disabled={disabled} value={line.unit} onChange={(e) => patch(line.key, { unit: e.target.value })} className={field} /> : (line.unit || '-')}
                    </td>
                    <td className="line-items-cell">
                      <Input
                        aria-label={`Quantity ${index + 1}`}
                        inputMode="decimal"
                        disabled={disabled}
                        value={line.quantity}
                        aria-invalid={line.quantity.trim() === '' || !/^\d*\.?\d*$/.test(line.quantity) || undefined}
                        onChange={(e) => patch(line.key, { quantity: e.target.value })}
                        onFocus={selectAll}
                        className={moneyInput}
                      />
                    </td>
                    <td className="line-items-cell">
                      <div className="flex items-center gap-1">
                        {/* the star's slot is always there, before the price; so every price box has the same width, the prices line up and the header can mirror it */}
                        <span className={STAR_SLOT}>
                          {preferred && (
                            <span title="Preferred vendor" aria-label="Preferred vendor" data-testid={`line-preferred-${line.key}`} style={{ color: colors.buttonSecondary }}>
                              <Star className="w-3 h-3 fill-current" />
                            </span>
                          )}
                        </span>
                        <Input
                          aria-label={`Unit price ${index + 1}`}
                          inputMode="decimal"
                          disabled={disabled}
                          value={line.unitPrice}
                          placeholder="0.00"
                          // red only for text that is not a number; an empty box simply counts as 0
                          aria-invalid={(line.unitPrice.trim() !== '' && !/^\d*\.?\d*$/.test(line.unitPrice)) || undefined}
                          // a price typed by hand is the user's own: it no longer comes from any vendor's offer
                          onChange={(e) => patch(line.key, { unitPrice: e.target.value, priceSource: 'manual' })}
                          onFocus={selectAll}
                        className={moneyInput}
                        />
                      </div>
                    </td>
                    <td className="line-items-cell text-right tabular-nums">{formatMoney(lineAmount(line))}</td>
                    {billing && (() => {
                      const pending = billing.pendingOf(line);
                      const text = line.billAmount.trim();
                      const invalid = text !== '' && parseNonNegative(text) === null;
                      const over = pending !== null && !invalid && (parseNonNegative(text) ?? 0) > pending + 0.000001;
                      return (
                        <td className="line-items-cell">
                          <Input
                            aria-label={`Bill amount ${index + 1}`}
                            inputMode="decimal"
                            disabled={disabled}
                            value={line.billAmount}
                            placeholder="0.00"
                            aria-invalid={invalid || over || undefined}
                            title={over ? 'More than what is still to bill on this line' : undefined}
                            onChange={(e) => patch(line.key, { billAmount: e.target.value })}
                            onFocus={selectAll}
                        className={moneyInput}
                          />
                          {pending !== null && <div className={`mt-0.5 text-right text-xs ${over ? 'text-destructive' : 'text-gray-500'}`} data-testid={`line-pending-${line.key}`}>Pending {formatMoney(pending)}</div>}
                        </td>
                      );
                    })()}
                    <td className="line-items-cell text-right">
                      <button type="button" className="generic-icon-button" title="Remove this item from the list" aria-label={`Remove item ${index + 1}`} disabled={disabled} onClick={() => remove(line, index)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lines.length > 0 && (adjustments ? (
        // Subtotal, Tax, Other and Total: the same block for every document that has them (the server repeats the arithmetic and stores ITS total)
        <div className="ml-auto w-full md:w-96 space-y-2 text-sm" data-testid="line-items-totals">
          <div className="grid grid-cols-[1fr,6rem,6rem] items-center gap-3">
            <span className="justify-self-end text-gray-600">Subtotal:</span>
            <span />
            <span className="tabular-nums text-right" data-testid="totals-subtotal">{formatMoney(totals.subtotal)}</span>
          </div>
          <div className="grid grid-cols-[1fr,6rem,6rem] items-center gap-3">
            <label htmlFor="line_items_tax_pct" className="justify-self-end text-gray-600">Tax (%):</label>
            <Input id="line_items_tax_pct" inputMode="decimal" disabled={disabled} value={adjustments.taxPct} placeholder="0" aria-invalid={taxInvalid || undefined} onChange={(e) => adjustments.onTaxPctChange(e.target.value)} onFocus={selectAll}
                        className={moneyInput} />
            <span className="tabular-nums text-right" data-testid="totals-tax">{formatMoney(totals.tax)}</span>
          </div>
          <div className="grid grid-cols-[1fr,6rem,6rem] items-center gap-3">
            <label htmlFor="line_items_other_amount" className="justify-self-end text-gray-600">Other ($):</label>
            <Input id="line_items_other_amount" inputMode="decimal" disabled={disabled} value={adjustments.otherAmount} placeholder="0.00" aria-invalid={otherInvalid || undefined} onChange={(e) => adjustments.onOtherAmountChange(e.target.value)} onFocus={selectAll}
                        className={moneyInput} />
            <span className="tabular-nums text-right" data-testid="totals-other">{formatMoney(totals.other)}</span>
          </div>
          <div className="grid grid-cols-[1fr,6rem,6rem] items-center gap-3 border-t border-border pt-2 font-bold">
            <span className="justify-self-end">Total:</span>
            <span />
            <span className="tabular-nums text-right" data-testid="totals-total">{formatMoney(totals.total)}</span>
          </div>
        </div>
      ) : (
        // ONE row: the Total (a document without tax or other charges has nothing else to add)
        <div className="flex justify-end gap-6 font-bold" data-testid="line-items-totals">
          <span>{totalLabel}</span>
          <span className="tabular-nums w-28 text-right">{formatMoney(totals.total)}</span>
        </div>
      ))}
    </div>
  );
}
